import { Fragment, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { DevicePanel } from "../components/DevicePanel";
import { Check, X, Ban, Power, Trash2, Users, RefreshCw, Search, Settings2, KeyRound, Eye, CalendarClock } from "lucide-react";

const BADGE = {
  active: "bg-emerald-500/15 text-emerald-400",
  pending: "bg-amber-500/15 text-amber-400",
  rejected: "bg-red-500/15 text-red-400",
  disabled: "bg-slate-600/30 text-slate-400",
};

export default function AdminUsers() {
  const { isAdmin, loading } = useAuth();
  const [users, setUsers] = useState([]);
  const [features, setFeatures] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [licFilter, setLicFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [pw, setPw] = useState({});
  const [lic, setLic] = useState({});

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/auth/users"); setUsers(r.data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat user"); }
    finally { setBusy(false); }
  }, []);

  const loadFeatures = useCallback(async () => {
    try { const r = await api.get("/features"); setFeatures(r.data || []); } catch (e) {}
  }, []);

  useEffect(() => { if (isAdmin) { load(); loadFeatures(); } }, [isAdmin, load, loadFeatures]);

  const act = async (uid, path, ok) => {
    try { await api.post(`/auth/users/${uid}/${path}`); toast.success(ok); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const del = async (uid) => {
    if (!window.confirm("Hapus user ini?")) return;
    try { await api.delete(`/auth/users/${uid}`); toast.success("User dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const delAllPending = async () => {
    const list = users.filter((u) => u.status === "pending" && !u.is_admin);
    if (!list.length) { toast.info("Tidak ada user pending."); return; }
    if (!window.confirm(`Hapus ${list.length} user pending? Tindakan ini permanen.`)) return;
    setBusy(true);
    try {
      await Promise.all(list.map((u) => api.delete(`/auth/users/${u.user_id}`)));
      toast.success(`${list.length} user pending dihapus`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus sebagian user"); load(); }
    finally { setBusy(false); }
  };

  const grantedSet = (u) => (Array.isArray(u.features) ? u.features : features.map((f) => f.key));
  const toggleFeat = async (u, key) => {
    const cur = grantedSet(u);
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    try { await api.post(`/auth/users/${u.user_id}/features`, { features: next }); toast.success("Fitur diperbarui"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const setAllFeats = async (u, on) => {
    const next = on ? features.map((f) => f.key) : [];
    try { await api.post(`/auth/users/${u.user_id}/features`, { features: next }); toast.success(on ? "Semua fitur diaktifkan" : "Semua fitur dimatikan"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const revealPw = async (uid) => {
    try {
      const r = await api.get(`/auth/users/${uid}/password`);
      setPw((p) => ({ ...p, [uid]: r.data.password || "(tidak tersedia — user daftar sebelum fitur ini)" }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const licVal = (u) => (lic[u.user_id] !== undefined ? lic[u.user_id] : (u.license_expires_at || ""));
  const saveLicense = async (uid, val) => {
    try {
      await api.post(`/auth/users/${uid}/license`, { expires_at: val || "" });
      toast.success(val ? `Lisensi aktif s/d ${val}` : "Lisensi disetel tanpa batas");
      setLic((p) => { const n = { ...p }; delete n[uid]; return n; });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const plusYear = (u) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let base = today;
    if (u.license_expires_at) {
      const d = new Date(u.license_expires_at + "T00:00:00");
      if (!isNaN(d) && d > today) base = d; // extend from the old date if it's still in the future (no wasted days)
    }
    base.setFullYear(base.getFullYear() + 1);
    saveLicense(u.user_id, base.toISOString().slice(0, 10));
  };

  if (!loading && !isAdmin) return <div className="p-10 text-center text-slate-500">Khusus admin.</div>;

  const s = q.trim().toLowerCase();
  const isExpiring = (u) => !u.is_admin && !u.license_expired && u.license_expires_at && u.license_days_left != null && u.license_days_left <= 14;
  const isExpired = (u) => !u.is_admin && u.license_expired;
  const expiringCount = users.filter(isExpiring).length;
  const expiredCount = users.filter(isExpired).length;
  const pendingCount = users.filter((u) => u.status === "pending" && !u.is_admin).length;
  let filtered = s
    ? users.filter((u) => [u.name, u.email, u.phone, u.country, u.status].some((x) => (x || "").toLowerCase().includes(s)))
    : users;
  if (licFilter === "expiring") filtered = filtered.filter(isExpiring);
  else if (licFilter === "expired") filtered = filtered.filter(isExpired);

  return (
    <div className="p-4 sm:p-8 max-w-[1200px] mx-auto" data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400/90 flex items-center gap-2"><Users className="h-4 w-4" /> Admin</div>
          <h1 className="font-head font-black text-4xl uppercase tracking-tight mt-1">Kontrol User</h1>
          <p className="text-slate-500 text-sm mt-1">Setujui, atur fitur, lihat password, atau hapus akun.</p>
        </div>
        <Button data-testid="admin-users-refresh" onClick={load} disabled={busy} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 pointer-events-none" />
        <input data-testid="admin-users-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Cari nama, email, HP, country, atau status…"
          className="w-full pl-9 pr-9 py-2 rounded-lg bg-[#0c1017] border border-slate-800 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/60" />
        {q && <button data-testid="admin-users-search-clear" onClick={() => setQ("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-500 hover:text-slate-200"><X className="h-4 w-4" /></button>}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="license-filter-bar">
        <span className="text-[11px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Lisensi:</span>
        <button data-testid="license-filter-all" onClick={() => setLicFilter("all")} className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition ${licFilter === "all" ? "bg-cyan-500 text-slate-950" : "bg-[#0c1017] border border-slate-800 text-slate-300 hover:border-slate-700"}`}>Semua</button>
        <button data-testid="license-filter-expiring" onClick={() => setLicFilter("expiring")} className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition ${licFilter === "expiring" ? "bg-amber-500 text-slate-950" : "bg-[#0c1017] border border-slate-800 text-amber-300/90 hover:border-amber-700/60"}`}>Hampir Habis{expiringCount ? ` (${expiringCount})` : ""}</button>
        <button data-testid="license-filter-expired" onClick={() => setLicFilter("expired")} className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition ${licFilter === "expired" ? "bg-red-600 text-white" : "bg-[#0c1017] border border-slate-800 text-red-300/90 hover:border-red-700/60"}`}>Sudah Habis{expiredCount ? ` (${expiredCount})` : ""}</button>
        <span className="mx-1 h-5 w-px bg-slate-800" />
        <button data-testid="admin-delete-pending" onClick={delAllPending} disabled={busy || !pendingCount} title="Hapus semua user pending sekaligus" className="text-xs px-3 py-1.5 rounded-lg font-semibold transition inline-flex items-center gap-1.5 bg-red-900/60 border border-red-800/60 text-red-200 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 className="h-3.5 w-3.5" /> Hapus Semua Pending{pendingCount ? ` (${pendingCount})` : ""}</button>
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0c1017] overflow-auto max-h-[70vh]" data-testid="admin-users-scroll">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-[#0c1017]">
            <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
              <th className="px-4 py-3">Nama</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">HP</th>
              <th className="px-4 py-3">Country</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const granted = grantedSet(u);
              const open = openId === u.user_id;
              return (
                <Fragment key={u.user_id}>
                  <tr data-testid={`user-row-${u.email}`} className="border-b border-slate-800/60">
                    <td className="px-4 py-2.5 text-slate-200 font-semibold">{u.name || "—"} {u.is_admin && <span className="text-[10px] text-emerald-400">(admin)</span>}</td>
                    <td className="px-4 py-2.5 text-slate-400 font-mono-tt text-xs">{u.email}</td>
                    <td className="px-4 py-2.5 text-slate-400 font-mono-tt text-xs">{u.phone || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-400">{u.country || "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded text-[11px] font-bold ${BADGE[u.status] || "bg-slate-700/40 text-slate-400"}`}>{u.status}</span>
                      {!u.is_admin && Object.keys(u.device_pending || {}).length > 0 && <span data-testid={`hwid-pending-badge-${u.email}`} className="ml-1.5 px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-300">HWID menunggu</span>}
                      {u.dev_access && <span data-testid={`dev-badge-${u.email}`} className="ml-1.5 px-2 py-0.5 rounded text-[10px] font-bold bg-sky-500/20 text-sky-300">develope</span>}
                      {u.reset_requested && <span data-testid={`reset-badge-${u.email}`} className="ml-1.5 px-2 py-0.5 rounded text-[10px] font-bold bg-red-500/20 text-red-300 inline-flex items-center gap-1"><KeyRound className="h-3 w-3" /> lupa pw</span>}
                      {!u.is_admin && (
                        u.license_expires_at
                          ? <div data-testid={`license-badge-${u.email}`} className={`mt-1 text-[10px] font-semibold inline-flex items-center gap-1 ${u.license_expired ? "text-cyan-300" : "text-slate-500"}`}><CalendarClock className="h-3 w-3" />{u.license_expired ? `Lisensi habis ${u.license_expires_at}` : `Lisensi s/d ${u.license_expires_at}`}</div>
                          : <div className="mt-1 text-[10px] text-slate-600 inline-flex items-center gap-1"><CalendarClock className="h-3 w-3" />Lisensi tanpa batas</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        {u.status !== "active" && <button data-testid={`approve-${u.email}`} title="Setujui" onClick={() => act(u.user_id, "approve", "Disetujui")} className="p-1.5 rounded bg-emerald-600/80 hover:bg-emerald-500 text-slate-950"><Check className="h-3.5 w-3.5" /></button>}
                        {u.status === "pending" && <button data-testid={`reject-${u.email}`} title="Tolak" onClick={() => act(u.user_id, "reject", "Ditolak")} className="p-1.5 rounded bg-cyan-500/80 hover:bg-cyan-400 text-slate-950"><X className="h-3.5 w-3.5" /></button>}
                        {u.status === "active" && !u.is_admin && <button data-testid={`disable-${u.email}`} title="Nonaktifkan" onClick={() => act(u.user_id, "disable", "Dinonaktifkan")} className="p-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100"><Ban className="h-3.5 w-3.5" /></button>}
                        {u.status === "disabled" && <button title="Aktifkan" onClick={() => act(u.user_id, "enable", "Diaktifkan")} className="p-1.5 rounded bg-emerald-600/80 hover:bg-emerald-500 text-slate-950"><Power className="h-3.5 w-3.5" /></button>}
                        {!u.is_admin && <button data-testid={`manage-${u.email}`} title="Atur fitur & password" onClick={() => setOpenId(open ? null : u.user_id)} className={`p-1.5 rounded ${open ? "bg-cyan-500 text-slate-950" : "bg-sky-600/80 hover:bg-sky-500 text-slate-950"}`}><Settings2 className="h-3.5 w-3.5" /></button>}
                        {!u.is_admin && <button data-testid={`delete-${u.email}`} title="Hapus" onClick={() => del(u.user_id)} className="p-1.5 rounded bg-red-900/60 hover:bg-red-700 text-red-200"><Trash2 className="h-3.5 w-3.5" /></button>}
                      </div>
                    </td>
                  </tr>
                  {open && !u.is_admin && (
                    <tr key={`${u.user_id}-panel`} data-testid={`manage-panel-${u.email}`} className="border-b border-slate-800/60 bg-[#0a0e14]">
                      <td colSpan={6} className="px-4 py-4">
                        <div className="flex flex-col lg:flex-row gap-6">
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Fitur aktif untuk user ini</div>
                              <div className="flex gap-1.5">
                                <button data-testid={`feat-all-on-${u.email}`} onClick={() => setAllFeats(u, true)} className="text-[11px] px-2 py-0.5 rounded bg-emerald-600/70 hover:bg-emerald-500 text-slate-950 font-bold">Semua ON</button>
                                <button data-testid={`feat-all-off-${u.email}`} onClick={() => setAllFeats(u, false)} className="text-[11px] px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 font-bold">Semua OFF</button>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {features.map((f) => {
                                const on = granted.includes(f.key);
                                return (
                                  <label key={f.key} data-testid={`feat-${u.email}-${f.key}`} onClick={() => toggleFeat(u, f.key)}
                                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition ${on ? "border-emerald-600/50 bg-emerald-500/10" : "border-slate-800 bg-[#0c1017] hover:border-slate-700"}`}>
                                    <span className={`h-4 w-4 rounded flex items-center justify-center ${on ? "bg-emerald-500" : "bg-slate-700"}`}>{on && <Check className="h-3 w-3 text-slate-950" />}</span>
                                    <span className={`text-sm ${on ? "text-slate-100" : "text-slate-400"}`}>{f.label}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                          <DevicePanel u={u} reload={load} />
                          <div className="lg:w-72 shrink-0">
                            <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Password user</div>
                            {pw[u.user_id] ? (
                              <div data-testid={`pw-value-${u.email}`} className="rounded-lg border border-red-900/40 bg-[#0c1017] px-3 py-2.5 font-mono-tt text-sm text-red-300 break-all select-all">{pw[u.user_id]}</div>
                            ) : (
                              <button data-testid={`reveal-pw-${u.email}`} onClick={() => revealPw(u.user_id)} className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm font-semibold">
                                <Eye className="h-4 w-4" /> Lihat Password
                              </button>
                            )}
                            <p className="text-[11px] text-slate-600 mt-2">Berikan password ini ke user lewat WhatsApp jika mereka lupa.</p>

                            <div className="mt-5">
                              <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Masa Lisensi (annual fee)</div>
                              <input type="date" data-testid={`license-input-${u.email}`} value={licVal(u)}
                                onChange={(e) => setLic((p) => ({ ...p, [u.user_id]: e.target.value }))}
                                className="w-full px-3 py-2 rounded-lg bg-[#0c1017] border border-slate-800 text-sm text-slate-100 focus:outline-none focus:border-cyan-400/60" />
                              <div className="flex gap-1.5 mt-2">
                                <button data-testid={`license-save-${u.email}`} onClick={() => saveLicense(u.user_id, licVal(u))} className="flex-1 text-[11px] px-2 py-1.5 rounded bg-emerald-600/80 hover:bg-emerald-500 text-slate-950 font-bold">Simpan</button>
                                <button data-testid={`license-year-${u.email}`} onClick={() => plusYear(u)} title="Tambah 1 tahun dari tanggal expired lama (jika masih aktif) atau dari hari ini" className="text-[11px] px-2 py-1.5 rounded bg-sky-600/80 hover:bg-sky-500 text-slate-950 font-bold">+1 Tahun</button>
                                <button data-testid={`license-clear-${u.email}`} onClick={() => saveLicense(u.user_id, "")} className="text-[11px] px-2 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 font-bold">Tanpa Batas</button>
                              </div>
                              <p className={`text-[11px] mt-2 ${u.license_expired ? "text-cyan-300" : "text-slate-600"}`}>
                                {u.license_expires_at ? (u.license_expired ? "Lisensi sudah habis — user terkunci sampai diperpanjang." : `Aktif sampai ${u.license_expires_at}.`) : "Tanpa batas waktu."}
                              </p>
                              {Array.isArray(u.license_history) && u.license_history.length > 0 && (
                                <div className="mt-4" data-testid={`license-history-${u.email}`}>
                                  <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Riwayat Perpanjangan</div>
                                  <div className="rounded-lg border border-slate-800 bg-[#0c1017] max-h-40 overflow-auto divide-y divide-slate-800/60">
                                    {u.license_history.slice().reverse().map((h, idx) => (
                                      <div key={idx} className="px-2.5 py-1.5 text-[11px] flex items-center justify-between gap-2">
                                        <span className="text-slate-300 font-mono-tt">{h.expires_at ? `→ ${h.expires_at}` : "→ tanpa batas"}</span>
                                        <span className="text-slate-600 shrink-0">{(h.set_at || "").slice(0, 10)}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {!filtered.length && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-600" data-testid="admin-users-empty">{s ? `Tidak ada user cocok dengan "${q}".` : "Belum ada user."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
